// Pure derivation of a contact's "Created" and "Last Changed" timestamps across
// the (possibly multiple) subscriptions that merge into one person's card.
//
//   Created      = EARLIEST added_at across the merged subscriptions (when the
//                  user first added this contact / their first profile from them).
//   Last Changed = LATEST content_changed_at across the merged subscriptions
//                  (the most recent real content/access change on ANY of them).
//
// content_changed_at may be missing on older rows predating the feature; we fall
// back to last_synced_at, then added_at, so a value is always derivable.

import { formatInZone } from './timezone.js';

export interface SubTimestamps {
  added_at?: string;
  last_synced_at?: string;
  content_changed_at?: string;
}

// Parse an RFC3339 string to epoch ms, or NaN if absent/unparseable.
function ms(s?: string): number {
  if (!s) return NaN;
  const t = Date.parse(s);
  return Number.isNaN(t) ? NaN : t;
}

// The best available "content changed" instant for a single subscription, with
// graceful fallback for rows that predate content_changed_at.
function changedMsFor(s: SubTimestamps): number {
  const c = ms(s.content_changed_at);
  if (!Number.isNaN(c)) return c;
  const l = ms(s.last_synced_at);
  if (!Number.isNaN(l)) return l;
  return ms(s.added_at);
}

// createdAt returns the earliest added_at (as an ISO string) across subs, or
// undefined if none carry a usable timestamp.
export function createdAt(subs: SubTimestamps[]): string | undefined {
  let best = Infinity;
  for (const s of subs) {
    const t = ms(s.added_at);
    if (!Number.isNaN(t) && t < best) best = t;
  }
  return best === Infinity ? undefined : new Date(best).toISOString();
}

// lastChangedAt returns the latest content-change instant (as an ISO string)
// across subs, applying the per-sub fallback, or undefined if none derivable.
export function lastChangedAt(subs: SubTimestamps[]): string | undefined {
  let best = -Infinity;
  for (const s of subs) {
    const t = changedMsFor(s);
    if (!Number.isNaN(t) && t > best) best = t;
  }
  return best === -Infinity ? undefined : new Date(best).toISOString();
}

// formatDateTime renders an ISO timestamp in the user's chosen display timezone
// with zone abbreviation + UTC offset, or '' when missing/unparseable. Delegates
// to the central timezone helper so display is consistent everywhere.
export function formatDateTime(iso?: string): string {
  if (!iso) return '';
  return formatInZone(iso);
}
